import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TypedDateInput } from "@/components/TypedDateInput";
import { todayISO } from "@/components/PermitCalculatedBadge";
import LocateDeadlineInput from "@/components/LocateDeadlineInput";
import { formatInformationalStatus } from "@/components/InformationalStatusText";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;

const LOCATE_CONDITIONS = ["valid", "trouble", "remark"];

export default function SupportRecordSheet({ mode, onSaved, open, record, onOpenChange }) {
  const [form, setForm] = useState(record || {});
  const isPermit = mode === "permit";
  const isPermitType = (form.permit_type || record?.permit_type) === "Permit";

  useEffect(() => setForm(record || {}), [record]);

  if (!record) return null;

  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const save = async (event) => {
    event.preventDefault();
    const path = isPermit ? `permits/${record.id}` : `communication-items/${record.id}`;
    const payload = { ...form };
    if (isPermit && isPermitType) {
      // Permit: humans record only the received fact; status is calculated.
      delete payload.status;
    }
    try {
      await axios.patch(`${API}/${path}`, payload);
      toast.success(`${isPermit ? "Permit / locate" : "Follow-up"} updated.`);
      onOpenChange(false);
      onSaved();
    } catch {
      toast.error("Could not save changes.");
    }
  };

  return <Sheet onOpenChange={onOpenChange} open={open}>
    <SheetContent className="record-sheet" data-testid={`${mode}-edit-sheet`}>
      <SheetHeader><SheetTitle data-testid={`${mode}-edit-title`}>{isPermit ? "Edit permit / locate" : "Edit communication item"}</SheetTitle><SheetDescription>Changes save directly to the parent project record.</SheetDescription></SheetHeader>
      <form className="record-sheet-form" onSubmit={save}>
        {isPermit ? <>
          <Field label="Type"><Input data-testid="permit-type-input" onChange={(event) => update("permit_type", event.target.value)} value={form.permit_type || ""} /></Field>
          <Field label="Reference number"><Input data-testid="permit-reference-input" onChange={(event) => update("reference_number", event.target.value)} value={form.reference_number || ""} /></Field>
          <Field label={isPermitType ? "Expires on" : "Valid through"}><TypedDateInput data-testid="permit-expires-input" disabled={isPermitType && Boolean(form.does_not_expire)} onChange={(value) => update("expires_on", value)} value={isPermitType && form.does_not_expire ? "" : (form.expires_on || "")} /></Field>
          {!isPermitType && <LocateDeadlineInput value={form.expires_at} onChange={(value) => update("expires_at", value)} />}
          {isPermitType
            ? <Field label="Received on"><TypedDateInput data-testid="permit-received-input" onChange={(value) => update("received_at", value)} value={form.received_at || ""} /><Button data-testid="permit-mark-received" onClick={() => update("received_at", todayISO())} type="button" variant="outline">Mark received today</Button><label className="master-editor-check-label"><input checked={Boolean(form.does_not_expire)} data-testid="permit-does-not-expire" onChange={(event) => { const c = event.target.checked; setForm((cur) => ({ ...cur, does_not_expire: c, expires_on: c ? "" : cur.expires_on })); }} type="checkbox" /><span>This permit does not expire</span></label></Field>
            : <Field label="Locate condition"><select data-testid="permit-status-select" onChange={(event) => update("status", event.target.value)} value={form.status || "valid"}>{!LOCATE_CONDITIONS.includes(form.status) && <option value={form.status}>{formatInformationalStatus(form.status)}</option>}<option value="valid">Valid</option><option value="trouble">Trouble</option><option value="remark">Remark</option></select></Field>}
        </> : <>
          <Field label="Task or conversation"><Input data-testid="communication-title-input" onChange={(event) => update("title", event.target.value)} value={form.title || ""} /></Field>
          <Field label="Assigned to"><Input data-testid="communication-assignee-input" onChange={(event) => update("assignee", event.target.value)} value={form.assignee || ""} /></Field>
          <Field label="Due date"><TypedDateInput data-testid="communication-due-input" onChange={(value) => update("due_at", value)} value={form.due_at || ""} /></Field>
          <Field label="Priority"><select data-testid="communication-priority-select" onChange={(event) => update("priority", event.target.value)} value={form.priority || "medium"}><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="urgent">Urgent</option></select></Field>
        </>}
        <Field label="Notes"><Textarea data-testid={`${mode}-notes-input`} onChange={(event) => update("notes", event.target.value)} value={form.notes || ""} /></Field>
        <Button data-testid={`save-${mode}-button`} type="submit">Save changes</Button>
      </form>
    </SheetContent>
  </Sheet>;
}

function Field({ children, label }) {
  return <div><Label>{label}</Label>{children}</div>;
}
